import React, { useEffect, useRef, useState } from "react";
import api from "../services/api";
import { Badge, Button, IconButton, SevBadge } from "./ui";
import { Icon } from "./Icon";
import { Logo, Wordmark } from "./ConsoleShell";
import { WorldMap } from "./GeoMap";
import "./LandingPage.css";

// RFC 5737 documentation addresses. This public preview is always labelled.
const SOURCES = [
  { ip: "203.0.113.24", lat: 55.75, lon: 37.62, country: "Russia", severity: "CRITICAL", count: 9 },
  { ip: "198.51.100.7", lat: 39.9, lon: 116.4, country: "China", severity: "HIGH", count: 6 },
  { ip: "192.0.2.61", lat: -23.55, lon: -46.63, country: "Brazil", severity: "HIGH", count: 4 },
  { ip: "203.0.113.90", lat: 52.37, lon: 4.9, country: "Netherlands", severity: "MEDIUM", count: 5 },
  { ip: "198.51.100.33", lat: 40.71, lon: -74, country: "United States", severity: "MEDIUM", count: 3 },
  { ip: "192.0.2.140", lat: 1.35, lon: 103.82, country: "Singapore", severity: "LOW", count: 2 },
];

function ConsolePreview() {
  return <div className="entry-preview" aria-label="Illustrative console preview with sample data">
    <div className="entry-preview-head"><span><Icon name="grid" size={13} />Security overview</span><Badge tone="neutral">Sample data</Badge></div>
    <div className="entry-preview-metrics"><div><span>Events analysed</span><strong>128,490</strong></div><div><span>Threats detected</span><strong>246</strong></div><div><span>Sources blocked</span><strong>38</strong></div></div>
    <div className="entry-map"><WorldMap sources={SOURCES} arcs={6} pulses={0} labels={false} illustrative ariaLabel="Illustrative threat origins using sample data" /><span className="entry-map-label"><Icon name="globe" size={11} />Global threat visibility</span></div>
    <div className="entry-preview-detection"><Icon name="radar" size={14} /><span><strong>Brute force attempt</strong><small className="mono">203.0.113.24</small></span><SevBadge sev="CRITICAL" /><Badge tone="ok" dot>Contained</Badge></div>
    <div className="entry-preview-detection"><Icon name="radar" size={14} /><span><strong>Suspicious port scan</strong><small className="mono">198.51.100.7</small></span><SevBadge sev="HIGH" /><Badge tone="ok" dot>Contained</Badge></div>
  </div>;
}


const CHAPTERS = [
  {
    id: "problem",
    kicker: "The challenge",
    title: "Attacks move in seconds. Security teams can't watch everything.",
    lead: "Organisations collect millions of log lines, sign-ins and network flows every day. Real attacks hide inside that noise, and by the time an analyst finds them by hand the damage is done.",
    points: [
      ["clock", "Speed", "Password guessing, scanning and injection attempts are automated and run around the clock."],
      ["layers", "Volume", "Too many alerts and too little context lead to alert fatigue, so real threats get missed."],
      ["trendDown", "Change", "Attackers keep changing their techniques, so a model trained once slowly goes out of date."],
    ],
  },
  {
    id: "solution",
    kicker: "Our solution",
    title: "One console that detects, explains and responds.",
    lead: "CyberShield is a security operations platform that watches activity as it happens, recognises attacks with machine learning, blocks dangerous sources automatically and gives analysts the full story in one place.",
    points: [
      ["radar", "Detect", "Every event is scored by an ensemble of machine-learning and deep-learning models and checked against live threat intelligence."],
      ["search", "Investigate", "Detections are grouped into campaigns, mapped to MITRE ATT&CK and explained, with an AI analyst on hand for questions."],
      ["shield", "Respond", "High-risk sources are blocked within seconds by policy, and analysts can block or release any source with one click."],
    ],
  },
];

const PIPELINE = [
  ["database", "Collect", "Events stream in through a secure ingest API, cloud VPC flow logs, the protected banking portal and a decoy admin portal (honeypot) that catches intruders."],
  ["filter", "Normalise and enrich", "Each event is converted to one common format, located with GeoIP and checked against eight threat-intelligence feeds, including abuse.ch Feodo Tracker, Spamhaus DROP, Tor exit nodes and OpenPhish."],
  ["cpu", "Machine-learning detection", "Random Forest and Gradient Boosting classify the attack type. An Isolation Forest flags behaviour never seen before. SHAP values explain which features drove each decision."],
  ["brain", "Deep learning", "An autoencoder learns normal traffic and measures how far each event departs from it. An LSTM reads log sequences, a CNN inspects file bytes for malware, and a reinforcement-learning (DQN) agent recommends the best response."],
  ["target", "Correlate and score", "Detections are mapped to MITRE ATT&CK techniques and linked into multi-stage campaigns along the kill chain. Each source receives a live risk score."],
  ["zap", "Respond automatically", "Critical and high-severity sources are blocked for eight minutes; a source with four detections in ten minutes is blocked as a repeat offender. Alerts go to Slack, Microsoft Teams and PagerDuty."],
  ["message", "Investigate", "Analysts hunt through stored events, open a full attacker profile, follow attacks on the live war-room map, and ask the AI analyst (powered by Anthropic Claude) for summaries, Sigma rules and remediation steps."],
  ["fileText", "Report and audit", "One click produces PDF or Word reports for leadership, incident files and ISO 27001 / NIST reviews. Every sign-in and action is written to a tamper-evident, HMAC-chained audit trail."],
];

const DRIFT = [
  ["ADWIN", "Adaptive windowing that detects when the error rate changes."],
  ["DDM and EDDM", "Track prediction errors and warn before accuracy drops."],
  ["Page-Hinkley", "A cumulative test that catches gradual shifts in anomaly scores."],
];

const SECURITY = [
  ["lock", "AES-256-GCM encryption", "Sensitive data is encrypted with authenticated encryption, using keys derived through HKDF and versioned for rotation."],
  ["key", "Argon2id passwords", "Console passwords are hashed with Argon2id, the winner of the Password Hashing Competition."],
  ["clipboard", "Tamper-evident audit", "Each audit entry is chained to the previous one with an HMAC, so any change is detectable."],
  ["shield", "Hardened web layer", "HTTPS only with HSTS, a strict Content-Security-Policy, rate limiting and sign-in lockout."],
];

const OFFER = [
  ["grid", "Live security console", "A real-time view of threats, sources and response actions, with a Basic view for everyone and an Advanced view for analysts."],
  ["bell", "Automated response", "Policy-driven blocking and instant notifications to the tools your team already uses."],
  ["sparkle", "AI-assisted analysis", "Plain-language answers about what is happening and what to do next."],
  ["fileText", "Reporting and compliance", "Ready-made reports for executives, incident response and ISO 27001 / NIST self-assessment."],
];

const STACK = ["React", "FastAPI (Python)", "PostgreSQL 16", "Redis 7", "scikit-learn", "PyTorch", "Docker Compose", "nginx with TLS", "AWS EC2"];

const ROADMAP = ["Adversarial-robustness testing of the detection models", "Multi-factor sign-in and role-based access", "Automatic retraining in production when drift is confirmed"];

function Chapter({ n, id, kicker, title, lead, children }) {
  return (
    <section className="lp-chapter" id={id} aria-labelledby={`${id}-title`}>
      <div className="lp-inner">
        <div className="lp-head">
          <span className="lp-num">{String(n).padStart(2, "0")}</span>
          <span className="lp-kicker">{kicker}</span>
        </div>
        <h2 id={`${id}-title`}>{title}</h2>
        {lead && <p className="lp-lead">{lead}</p>}
        {children}
      </div>
    </section>
  );
}

function Points({ items }) {
  return (
    <div className="lp-points">
      {items.map(([icon, title, text]) => (
        <article key={title}>
          <Icon name={icon} size={18} />
          <h3>{title}</h3>
          <p>{text}</p>
        </article>
      ))}
    </div>
  );
}

function Story() {
  return (
    <div className="lp-story">
      {CHAPTERS.map((c, i) => (
        <Chapter key={c.id} n={i + 1} {...c}>
          <Points items={c.points} />
        </Chapter>
      ))}
      <Chapter n={3} id="pipeline" kicker="How it works" title="From raw event to response in eight steps." lead="Every event takes the same path through the platform. Here it is, step by step.">
        <ol className="lp-pipeline">
          {PIPELINE.map(([icon, title, text], i) => (
            <li key={title}>
              <span className="lp-step">{i + 1}</span>
              <div>
                <h3><Icon name={icon} size={16} />{title}</h3>
                <p>{text}</p>
              </div>
            </li>
          ))}
        </ol>
      </Chapter>
      <Chapter n={4} id="drift" kicker="Staying accurate" title="Concept-drift detection keeps the models accurate." lead="Attack patterns change over time, and a model that was accurate last month can quietly fall behind. CyberShield's research module watches the models' error rates and anomaly scores with established drift detectors and signals when retraining is needed. It is evaluated by replaying the CIC-IDS2017 intrusion dataset.">
        <div className="lp-drift">
          {DRIFT.map(([name, text]) => (
            <div key={name}>
              <strong>{name}</strong>
              <span>{text}</span>
            </div>
          ))}
        </div>
      </Chapter>
      <Chapter n={5} id="security" kicker="Security by design" title="The platform protects itself, too." lead="A security tool has to be a hard target. CyberShield is built against the OWASP Top 10 and uses modern, well-reviewed cryptography throughout.">
        <Points items={SECURITY} />
      </Chapter>
      <Chapter n={6} id="offer" kicker="What we offer" title="Everything a security team needs in one place.">
        <Points items={OFFER} />
      </Chapter>
      <Chapter n={7} id="stack" kicker="Architecture" title="Built on proven, open technology." lead="CyberShield runs as containerised services on AWS behind an HTTPS-only nginx gateway. The API, database, cache and web console run as separate services.">
        <ul className="lp-stack">
          {STACK.map((t) => <li key={t}>{t}</li>)}
        </ul>
        <div className="lp-roadmap">
          <h3>Next on the roadmap</h3>
          <ul>{ROADMAP.map((t) => <li key={t}>{t}</li>)}</ul>
        </div>
      </Chapter>
    </div>
  );
}

export default function LandingPage({ theme, onToggleTheme, onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [caps, setCaps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const passRef = useRef(null);
  // Smooth in-page scrolling for the presentation menu, only while this page is open
  useEffect(() => {
    const root = document.documentElement;
    root.style.scrollBehavior = "smooth";
    return () => { root.style.scrollBehavior = ""; };
  }, []);
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!username.trim() || !password) { setError("Enter your username and password."); return; }
    setBusy(true); setError("");
    try {
      const response = await api.post("/api/auth/login", { username: username.trim(), password });
      if (!response.data?.token) throw new Error("No session was returned.");
      onLogin(response.data.token);
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 401 ? "Incorrect username or password." : status === 429 ? err.response.data?.detail || "Too many attempts. Wait a moment and try again." : !err?.response ? "Unable to reach the server. Check your connection and try again." : err.response.data?.detail || "Sign-in failed. Try again.");
      setPassword(""); passRef.current?.focus();
    } finally { setBusy(false); }
  };
  return <div className="entry">
    <a className="skip-link" href="#sign-in">Skip to sign in</a>
    <header className="entry-header">
      <div className="entry-brand"><Logo /><Wordmark /></div>
      <nav className="lp-nav" aria-label="Page sections"><a href="#problem">Challenge</a><a href="#pipeline">How it works</a><a href="#drift">Drift detection</a><a href="#security">Security</a><a href="#stack">Architecture</a></nav>
      <div className="entry-header-right"><a className="lp-signin-link" href="#sign-in">Sign in</a><IconButton icon={theme === "dark" ? "sun" : "moon"} label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"} onClick={onToggleTheme} /></div>
    </header>
    <main>
      <section className="lp-hero" aria-labelledby="entry-title">
        <div className="lp-hero-copy">
          <div className="entry-kicker"><i />AI-POWERED SECURITY OPERATIONS</div>
          <h1 id="entry-title">See every threat.<br /><span>Stop it in seconds.</span></h1>
          <p className="lp-hero-intro">CyberShield brings detection, investigation and response together in one console. Machine learning finds the attacks, policy blocks them, and analysts get the full picture.</p>
          <div className="lp-hero-actions"><a className="btn btn-primary" href="#problem">See how it works</a><a className="btn btn-ghost" href="#sign-in">Sign in to the console</a></div>
          <ul className="lp-hero-facts"><li><strong>8</strong>threat-intelligence feeds</li><li><strong>7</strong>ML and deep-learning models</li><li><strong>4</strong>drift detectors</li></ul>
        </div>
        <ConsolePreview />
      </section>
      <Story />
      <section className="entry-access lp-signin" id="sign-in" aria-labelledby="sign-in-title">
        <div className="entry-access-inner"><span className="entry-lock"><Icon name="lock" size={21} /></span>
          <div className="entry-access-heading"><span className="entry-kicker">LIVE DEMONSTRATION</span><h2 id="sign-in-title">Now let's see it live.</h2><p>Sign in to the CyberShield console.</p></div>
          <form className="entry-form" onSubmit={submit} noValidate>
            {error && <div className="entry-error" role="alert"><Icon name="alertCircle" size={16} /><span>{error}</span></div>}
            <label className="field"><span className="field-label">Username</span><input className="input" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Enter your username" autoComplete="username" autoCapitalize="none" spellCheck={false} required disabled={busy} /></label>
            <label className="field"><span className="field-label">Password</span><span className="entry-password"><input ref={passRef} className="input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} onKeyUp={(e) => setCaps(!!e.getModifierState?.("CapsLock"))} placeholder="Enter your password" autoComplete="current-password" required disabled={busy} /><IconButton icon={show ? "eyeOff" : "eye"} label={show ? "Hide password" : "Show password"} onClick={() => setShow((value) => !value)} size={16} /></span>{caps && <span className="field-hint">Caps Lock is on</span>}</label>
            <Button type="submit" variant="primary" iconRight="arrowRight" loading={busy} disabled={busy} className="entry-submit">{busy ? "Signing in…" : "Sign in to workspace"}</Button>
          </form>
          <div className="entry-access-note"><Icon name="shield" size={14} /><span>Access is restricted to authorised users.<br />Sign-in activity is recorded in the audit trail.</span></div>
        </div>
      </section>
    </main>
    <footer className="entry-footer"><span>© {new Date().getFullYear()} CyberShield</span><details><summary>Khalifa University · Senior Design Project <Icon name="chevronDown" size={12} /></summary><p>A research prototype. Demonstration traffic may be simulated and is labelled in the console. Model metrics use synthetic data; compliance views are self-assessments. The platform has not been independently audited.</p></details><span>Security operations, connected.</span></footer>
  </div>;
}
